import {
  Box,
  Collapse,
  ExpandMoreRoundedIcon,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Rotate,
  Text,
  styled,
  type ListItemButtonProps,
} from '@shared/components';
import { Link, matchPath, useLocation, useMatch, useResolvedPath } from 'react-router-dom';

import type { MenuItem } from '../../hooks';

import TooltipMenu from './tooltip-menu';

const SmallDot = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  backgroundColor: 'transparent',
  position: 'relative',
  '&::before': {
    content: '""',
    width: 4,
    height: 4,
    borderRadius: theme.shape.rounded('50%'),
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.grey[600],
  },
}));

type NavMenuItemProps = Pick<MenuItem, 'label' | 'icon' | 'link' | 'subMenu'> & {
  collapsible?: boolean;
  open?: boolean;
  minified?: boolean;
} & ListItemButtonProps;

function NavMenuItem(props: Readonly<NavMenuItemProps>) {
  const {
    icon: iconProp,
    label: labelProp,
    link = '',
    open: isOpen = false,
    subMenu,
    collapsible: isCollapsible,
    minified: isMinified,
  } = props;

  const { pathname: currentPath } = useLocation();

  const target = useResolvedPath(link);

  // If not match with user's current or nested route, route's match object will be null.
  const targetPageMatch = useMatch({ path: `${target.pathname}` });
  const targetOrNestedPageMatch = useMatch({ path: `${target.pathname}/*` });

  const isAlreadyOnTargetPage = !!targetPageMatch;
  const isAlreadyOnTargetOrNestedPage = !!targetOrNestedPageMatch;

  let icon;
  let label;
  let tooltipContent;

  if (iconProp) {
    icon = <ListItemIcon>{iconProp}</ListItemIcon>;
  } else {
    icon = (
      <ListItemIcon
        sx={
          isAlreadyOnTargetOrNestedPage
            ? {
                '& > div::before': {
                  backgroundColor: (theme) => theme.palette.primary.main,
                  width: 8,
                  height: 8,
                },
              }
            : {}
        }
      >
        <SmallDot />
      </ListItemIcon>
    );
  }

  if (labelProp && !isMinified) {
    if (typeof labelProp === 'string') {
      label = <Text variant='body2'>{labelProp}</Text>;
    } else {
      label = labelProp;
    }
  }

  // used for rendering TooltipMenu
  const isAccordionMenu = !!subMenu;
  const selectedSubMenu = subMenu?.find((menu) => {
    const targetIsSubMenu = matchPath(`${menu.link}`, currentPath);
    const targetIsUnderSubMenu = matchPath(`${menu.link}/*`, currentPath);

    return targetIsSubMenu || targetIsUnderSubMenu;
  });
  const isSelectingSubMenu = !!selectedSubMenu;

  if (isAccordionMenu) {
    // tooltip with submenu
    tooltipContent = (
      <>
        <ListItemText>
          <Text variant='body1' fontWeight={600}>
            {labelProp}
          </Text>
        </ListItemText>
        {subMenu.map((item) => (
          <ListItemButton
            key={item.label as string}
            selected={selectedSubMenu?.link === item.link}
            component={Link}
            to={item.link as string}
            sx={{ width: 200 }}
            onClick={
              currentPath === item.link
                ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
                : undefined
            }
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <Text variant='body2'>{item.label}</Text>
          </ListItemButton>
        ))}
      </>
    );
  } else {
    // basic tooltip
    tooltipContent = (
      <ListItemText>
        <Text variant='body1' fontWeight={600}>
          {labelProp}
        </Text>
      </ListItemText>
    );
  }

  return isCollapsible ? (
    <Collapse in={!isMinified}>
      <ListItemButton
        selected={isAlreadyOnTargetOrNestedPage}
        component={Link}
        to={link}
        onClick={
          isAlreadyOnTargetPage
            ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
            : undefined
        }
      >
        {icon}
        {label}
      </ListItemButton>
    </Collapse>
  ) : (
    <TooltipMenu
      title={isMinified ? tooltipContent : ''}
      leaveDelay={isAccordionMenu ? 150 : 0}
      enterTouchDelay={isAccordionMenu ? 0 : 700}
      leaveTouchDelay={isAccordionMenu ? 3000 : 1500}
    >
      <ListItemButton
        selected={isAccordionMenu ? isSelectingSubMenu : isAlreadyOnTargetOrNestedPage}
        sx={{ borderRadius: (theme) => theme.shape.rounded(1.5) }}
        component={isAccordionMenu ? 'li' : Link}
        to={link}
        disableRipple={isAccordionMenu && isMinified}
        onClick={
          isAlreadyOnTargetPage
            ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
            : undefined
        }
      >
        {icon}
        {label}
        {isAccordionMenu ? (
          <Rotate in={!isOpen}>
            <ExpandMoreRoundedIcon
              sx={{
                display: isMinified ? 'none' : 'block',
                ml: 'auto',
              }}
            />
          </Rotate>
        ) : null}
      </ListItemButton>
    </TooltipMenu>
  );
}

export type { NavMenuItemProps };
export default NavMenuItem;
